// Phase 4d — Modification Center.
//
// The review/commit surface for a contract's modifications, rendered as a tab in
// Contract Workspace V2. reverie's pipeline stages a Modification ContractChangeSet
// when a mod document is processed (AnalyzeModificationV2 -> BeginModificationComparison);
// this screen lists those sets, lets a reviewer resolve/route each field-level
// change item, and commits the set (CommitModification -> pangaea ApplyChangeSet).
//
// The change-item RPCs are generic over change_set_id / item id, so the list /
// resolve / route hooks are reused verbatim from the award-comparison workspace.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraDataTable, SpecraFormField, SpecraIconButton, SpecraInlineBanner, SpecraInput, SpecraPanelHeader, SpecraSectionHeader, SpecraSegmentedControl, SpecraSelect, SpecraSurface, SpecraText, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Eye, RefreshCw, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DocumentViewerDrawer } from '../../../../components/ProposalDashboard/DocumentViewer/DocumentViewerDrawer';
import { useAuth, useIsViewOnly } from '../../../../contexts/authContext';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { formatUtcYmd } from '../../_shared/api/nullable';
import { ReviewSourceChip } from '../../_shared/components/ReviewSourceChip';
import { BlockingCommitList, itemsBlockingCommit, scrollToBlockingList } from '../../_shared/components/review/BlockingCommitList';
import { RESOLUTION_BADGE_VARIANTS, SEVERITY_BADGE_VARIANTS } from '../../_shared/components/review/badgeVariants';
import { DiscardChangeSetDialog } from '../../_shared/components/review/DiscardChangeSetDialog';
import { ReviewItemList } from '../../_shared/components/review/ReviewItemList';
import { ReviewItemDrawer } from '../../_shared/components/review/ReviewItemPanel';
import { approveAllSkippedNote, displayValue, hasNoWriter, isReviewItem, NO_WRITER_NOTE, needsDecisionNote, noWriterNeedsDecision, offeredDecisions, resolutionLabel, targetKind } from '../../_shared/components/review/reviewItemCopy';
import { groupReviewItems, isOpenEntry } from '../../_shared/components/review/reviewItemGroups';
import { StageContractLineDrawer } from '../../_shared/components/review/StageContractLineDrawer';
import { useTenantId } from '../../_shared/hooks/useTenantId';
import { correctedFieldKind, correctedValueJson } from '../../_shared/lib/correctedValue';
import { formatMoney, optionalTaskLabel, orderCeilingLabel, periodLabel } from '../../_shared/lib/recordLabels';
import { reviewSourceProps } from '../../_shared/lib/reviewSourceProps';
import { parseSpan } from '../../_shared/lib/sourceSpan';
import { useDuties } from '../../Deliverables/hooks';
import { useProgramDocuments } from '../../Docs/hooks';
import { useApproveAllDiscrepancies, useChangeSetDiscrepancies, useResolveDiscrepancy, useRouteDiscrepancy, useSetDiscrepancyTargetPeriod } from '../../PreAward/awardCompareHooks';
import { useContractCLINs } from '../contractHooks';
import { useProgram } from '../hooks';
import { popHooks } from '../operationalHooks';
import { useReextractContract } from '../sections/useReextractContract';
import { CommitRefusalPanel, highlightItemRow } from './CommitRefusalPanel';
import { commitRefusals, useCommitModification, useProgramModificationChangeSets } from './modificationHooks';
// An empty or zero amount shows a dash; every figure goes through the one
// Delivery money formatter (cents).
// The program's contract document: the latest one filed as Contract.
export function contractDocumentId(docs) {
    const contracts = docs.filter((d)=>d.category === 'Contract' && d.fileId);
    contracts.sort((a, b)=>(b.uploadedAt?.toDate().getTime() ?? 0) - (a.uploadedAt?.toDate().getTime() ?? 0));
    return contracts[0]?.fileId ?? '';
}
function moneyOrDash(n) {
    return n ? formatMoney(n) : '—';
}
function moneyStrOrDash(s) {
    const n = Number(s);
    return Number.isNaN(n) ? s : moneyOrDash(n);
}
const RESOLUTION_DECISIONS = [
    'Accepted',
    'Corrected',
    'Waived',
    'Rejected',
    'Escalated'
];
// The Approve all tooltip: what it accepts, and what it leaves for the
// reviewer — the items that need their call, and the ones no writer lands.
export function approveAllTitle(openReview, noWriter) {
    const parts = [
        'Accept every unsettled change item (admin only).'
    ];
    if (openReview > 0) {
        parts.push(`The ${openReview} that need your call ${openReview === 1 ? 'is' : 'are'} left for you to decide one by one.`);
    }
    if (noWriter > 0) {
        parts.push(`${noWriter} ${noWriter === 1 ? 'is' : 'are'} skipped: Specra can't write ${noWriter === 1 ? 'it' : 'them'} to the contract.`);
    }
    return parts.join(' ');
}
const FUNCTIONAL_OWNERS = [
    'Delivery',
    'Contracts',
    'Finance',
    'Subcontracts',
    'HrOnboarding',
    'Quality',
    'Deliverables',
    'GrowthHandoff'
];
export const ALREADY_APPLIED_NOTE = 'Already applied — nothing left to apply on this set.';
// Mirrors the apply loop (delivery_contract_change_repository.go): only an
// accepted decision is written through. Waived is settled but carries nothing
// to apply, and a Closed item never writes (D116), so neither is labeled
// "applies on commit". Commit settlement itself is itemsBlockingCommit
// (BlockingCommitList) — the same wire flags zou's gate reads.
const READY_TO_APPLY = new Set([
    'Accepted',
    'Corrected'
]);
// Baseline roles allowed to bulk-approve. Matches RequireAdminRoute.tsx /
// enies-lobby admin_authz.go::AdminRoles — keep in sync (Wave 3c retired
// tenant_security_officer). Settling a row needs change_set:commit (D94),
// which admins hold, so this is a UI gate on the convenience action, not a new
// privilege.
const ADMIN_ROLES = new Set([
    'admin',
    'internal'
]);
// JSON value cells (proposed/baseline) arrive as JSON text; never show braces
// (SAD-901). Team "name:" refs resolve via displayValue / teamRefLabel (SAD-914).
function fmtVal(s, field = '') {
    return displayValue(s ?? '', field);
}
// A value is "present" when it parses to something non-empty/non-null.
function hasVal(s) {
    if (!s) return false;
    try {
        const v = JSON.parse(s);
        return v !== null && v !== '' && !(Array.isArray(v) && v.length === 0);
    } catch  {
        return s.trim() !== '';
    }
}
const DATE_FIELDS = new Set([
    'pop_start_date',
    'pop_end_date',
    'start_date',
    'end_date'
]);
// A calendar date a value cell holds, or null.
function dayOf(s) {
    try {
        const v = JSON.parse(s ?? '');
        return typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : null;
    } catch  {
        return null;
    }
}
// Operation is derived from value presence (we don't store an explicit op):
// no before + after → Added; before + no after → Removed; both → Changed. A
// date with both its prior and new value reads as the window it makes:
// Extended (a later end, an earlier start) or Shortened.
export function changeOp(d) {
    const before = hasVal(d.baselineValueJson);
    const after = hasVal(d.proposedValueJson);
    if (!before && after) return 'Added';
    if (before && !after) return 'Removed';
    const field = targetFieldPath(d);
    const from = dayOf(d.baselineValueJson);
    const to = dayOf(d.proposedValueJson);
    if (DATE_FIELDS.has(field) && from && to && from !== to) {
        return to > from === field.endsWith('end_date') ? 'Extended' : 'Shortened';
    }
    return 'Changed';
}
const OP_BADGE_VARIANTS = {
    Added: 'success',
    Removed: 'danger',
    Changed: 'info',
    Extended: 'info',
    Shortened: 'warning'
};
// targetRecordLocatorJson is {field_path: object_id}; show the addressed field.
// The locator is {fieldPath: objectId}. Pull each out for grouping/labels.
function targetFieldPath(d) {
    try {
        const loc = JSON.parse(d.targetRecordLocatorJson || '{}');
        return Object.keys(loc)[0] || '';
    } catch  {
        return '';
    }
}
function targetObjectId(d) {
    try {
        const loc = JSON.parse(d.targetRecordLocatorJson || '{}');
        // Locator is {fieldPath: objectId}; for an object-add the key is "" (no
        // field), so read the VALUE — not loc[key] guarded on a truthy key.
        return Object.values(loc)[0] ?? '';
    } catch  {
        return '';
    }
}
// An object-level add carries no field path — the whole CLIN/SLIN is new.
function isObjectAdd(d) {
    return targetFieldPath(d) === '';
}
// Only these target types are contract line items with a CLIN-table row.
// Everything else — Contact (keyed by email), Company/ProgramCompanyAssignment
// (keyed by company name), PeriodOfPerformance, optional tasks, etc. — belongs
// in the contract-level section regardless of whether it carries a target id.
const CLIN_RECORD_TYPES = new Set([
    'CLIN',
    'ContractCLIN'
]);
function isClinTarget(d) {
    return CLIN_RECORD_TYPES.has(d.targetSdsRecordType);
}
function isContactTarget(d) {
    return d.targetSdsRecordType === 'Contact' || d.targetSdsRecordType === 'ProgramContact';
}
// "funded_amount" → "Funded amount".
function humanizeField(f) {
    return f.replace(/_/g, ' ').replace(/^\w/, (c)=>c.toUpperCase());
}
// Humanize a field path for display within a CLIN group ("funded_amount" →
// "Funded amount"). For an object-level add, show the new line item's title.
function fieldLabel(d) {
    // A ceiling the whole order carries reads by its kind: "T&M NTE ceiling".
    if (d.targetSdsRecordType === 'OrderCeiling') return orderCeilingLabel(targetObjectId(d));
    const f = targetFieldPath(d);
    if (!f) {
        if (isContactTarget(d)) return 'New point of contact';
        if (d.targetSdsRecordType === 'ProgramCompanyAssignment') return 'New company';
        if (d.targetSdsRecordType === 'Workstream') return 'New team';
        if (!isClinTarget(d)) return 'New record';
        try {
            const v = JSON.parse(d.proposedValueJson || '{}');
            if (v.title) return v.title;
        } catch  {
        /* fall through */ }
        return 'New line item';
    }
    return humanizeField(f);
}
// A PeriodOfPerformance item targets a contract period, not a line item: its
// target id is pangaea's period natural key and its field path is one of the
// four applyPeriodOfPerformanceField writes (start_date / end_date /
// funded_amount / total_amount).
function isPeriodTarget(d) {
    return d.targetSdsRecordType === 'PeriodOfPerformance';
}
// A ProgramTask item targets a deliverable (CDRL), not a line item: its
// target id is pangaea's ProgramTask natural key and its field path is one
// of the three fields baltigo's deliverable-change extractor emits
// (due_date / frequency / state — services/baltigo/pkg/govcon/
// mod_deliverable_change.go).
function isDeliverableTarget(d) {
    return d.targetSdsRecordType === 'ProgramTask';
}
// An OptionalTask item targets an option-exercise line, not a CLIN row: its
// target id is the bare task number baltigo's optionExerciseChanges emits as
// TargetObjectId, and its field path is always "exercised" (services/baltigo/
// pkg/govcon/contract_analyzer_v2.go). With PR 1's per-task extraction, a
// single modification can carry one of these per exercised task, so naming
// which task each row is about is the whole fix.
function isOptionalTaskTarget(d) {
    return d.targetSdsRecordType === 'OptionalTask';
}
// The money-valued period fields (baltigo's per-period funding-summary deltas);
// the date-valued ones — start_date / end_date — render as the ISO date.
const PERIOD_MONEY_FIELDS = new Set([
    'funded_amount',
    'total_amount'
]);
// Label a loaded period for the target-period picker's <option>: the name
// alone, or name + start->end when the period carries dates.
function periodOptionLabel(p) {
    const start = formatUtcYmd(p.startDate);
    const end = formatUtcYmd(p.endDate);
    return start || end ? `${p.periodName} (${start ?? '—'} → ${end ?? '—'})` : p.periodName;
}
// Humanize a ProgramTask's natural key into a deliverable label ("A001", or
// CLIN-qualified "0002:A001"), so the reviewer reads one name for one
// deliverable. An empty id or a dangling qualifier ("0002:" / ":A001") still
// gets a label rather than being dropped, the same rule periodLabel follows
// for an unattributed period.
function deliverableLabel(key) {
    const k = key.trim();
    if (!k) return 'Unattributed deliverable';
    const idx = k.indexOf(':');
    if (idx === -1) return `Deliverable ${k}`;
    const clin = k.slice(0, idx).trim();
    const deliverable = k.slice(idx + 1).trim();
    if (!clin || !deliverable) return `Deliverable ${k}`;
    return `CLIN ${clin} · Deliverable ${deliverable}`;
}
// A modification's deliverable change has no writer: Mint owns every date a
// modification moves (S5a), and accepting one refuses the commit by name
// (D87). The copy says so rather than promising a calendar effect.
const DELIVERABLE_CHANGE_UNWRITTEN = 'Specra doesn’t apply a modification’s deliverable changes yet: accepting this stops ' + 'the commit. Leave it blank with a reason; the deliverable’s dates stay on the contract ' + 'schedule.';
// For an object-level add, summarize the new line item (its obligated amount)
// rather than dumping the raw CLIN JSON.
function addSummary(d) {
    if (isContactTarget(d)) {
        try {
            const c = JSON.parse(d.proposedValueJson || '{}');
            const name = [
                c.first_name,
                c.last_name
            ].filter(Boolean).join(' ');
            const email = c.email || targetObjectId(d);
            const who = name ? `${name} <${email}>` : email;
            return c.role ? `${who} (${c.role})` : who;
        } catch  {
            return targetObjectId(d);
        }
    }
    // A settled team create's target id is a "name:" ref; show the printed
    // name from the payload (SAD-914).
    if (d.targetSdsRecordType === 'Workstream') {
        try {
            const v = JSON.parse(d.proposedValueJson || '{}');
            if (v.name?.trim()) return v.name.trim();
        } catch  {
        /* fall through */ }
        const id = targetObjectId(d);
        return id.startsWith('name:') ? id.slice('name:'.length).trim() || id : id;
    }
    if (!isClinTarget(d)) return targetObjectId(d);
    try {
        const v = JSON.parse(d.proposedValueJson || '{}');
        const funded = Number(v.funded_amount);
        if (funded && !Number.isNaN(funded)) return `${formatMoney(funded)} obligated`;
    } catch  {
    /* fall through */ }
    return 'new line item';
}
// The full captured detail of a newly-added CLIN/SLIN (object-add), so the
// reviewer sees everything — descriptor, PoP, type — not just the headline.
function parseNewClin(d) {
    if (!isObjectAdd(d) || !isClinTarget(d)) return null;
    try {
        return JSON.parse(d.proposedValueJson || '{}');
    } catch  {
        return null;
    }
}
// Collapse the extractor's blank-line/indent runs so the descriptor reads clean.
function cleanDescription(s) {
    return s.split('\n').map((line)=>line.trim()).filter(Boolean).join('\n');
}
// Group change items by their target object (CLIN id), so a CLIN with several
// changes reads as one block instead of scattered rows. CLINs (with an id)
// sort first by id; contract-level groups (no id) fall to the end.
function groupByTarget(items) {
    const byKey = new Map();
    const order = [];
    for (const d of items){
        const id = targetObjectId(d);
        const key = `${d.targetSdsRecordType}::${id}`;
        let g = byKey.get(key);
        if (!g) {
            g = {
                key,
                type: d.targetSdsRecordType,
                id,
                items: []
            };
            byKey.set(key, g);
            order.push(g);
        }
        g.items.push(d);
    }
    return order.sort((a, b)=>{
        if (!a.id !== !b.id) return a.id ? -1 : 1; // ided groups first
        return a.id.localeCompare(b.id, undefined, {
            numeric: true
        });
    });
}
export function rosterView(state) {
    if (state === 'Cancelled') return 'discarded';
    if (state === 'Superseded') return null;
    return 'active';
}
// A set no apply has reached yet, which a reviewer may still discard.
const DISCARDABLE_STATES = new Set([
    'Draft',
    'Parsed',
    'UnderReview',
    'Approved'
]);
// Who discarded the set, the way the History timeline names an actor
// (OpsAuditTimeline ResolvedActor): the resolved name; else an unresolved user
// id shortened; else the actor as written ("pipeline").
export function discardedBy(cs) {
    const id = cs.updatedBy;
    const name = cs.updatedByDisplayName?.trim();
    if (name && name !== id) return name;
    return /^[0-9a-f]{32}$/.test(id) ? `${id.slice(0, 8)}...` : id;
}
// "Discarded by Ada Quantum on 2026-10-23: wrong file", with the parts it has.
export function discardedNote(cs) {
    const when = formatUtcYmd(cs.updatedAt);
    const by = discardedBy(cs);
    const head = [
        'Discarded',
        by && `by ${by}`,
        when && `on ${when}`
    ].filter(Boolean).join(' ');
    return cs.stateReason ? `${head}: ${cs.stateReason}` : `${head}.`;
}
// The roster reviews Modification sets (a mod document processed),
// ReExtraction sets (a converging contract re-upload's proposed contact/
// company changes) and ContractIntake sets (the facts a contract upload
// couldn't settle, D93 path a; settled here per D94). AwardImport has its own
// review surface at PreAward/AwardComparisonPage.tsx and must never surface
// here.
const ROSTER_CHANGE_SET_TYPES = new Set([
    'Modification',
    'ReExtraction',
    'ContractIntake'
]);
const CHANGE_SET_TYPE_LABELS = {
    Modification: 'Modification',
    ReExtraction: 'Re-extraction',
    ContractIntake: 'Contract intake'
};
export function ModificationCenter({ programId }) {
    const tenantId = useTenantId();
    const { canEdit } = useProgramContext();
    const { reextract, running: reextractRunning } = useReextractContract();
    const { data: program } = useProgram(programId);
    const zouProjectId = program?.program?.zouProjectId ?? '';
    const { data: allChangeSets, isLoading } = useProgramModificationChangeSets(programId, tenantId);
    const [viewingDoc, setViewingDoc] = useState(null);
    const [view, setView] = useState('active');
    const [discarding, setDiscarding] = useState(null);
    const rostered = (allChangeSets ?? []).filter((cs)=>ROSTER_CHANGE_SET_TYPES.has(cs.changeSetType));
    const countIn = (v)=>rostered.filter((cs)=>rosterView(cs.state) === v).length;
    const changeSets = rostered.filter((cs)=>rosterView(cs.state) === view);
    // ?set=<change set>&item=<item> opens one question (a deliverable held by a
    // PWS condition links straight to the question that holds it). Read once,
    // as the page opens.
    const [linked] = useState(()=>{
        const q = new URLSearchParams(window.location.search);
        return {
            set: q.get('set'),
            item: q.get('item')
        };
    });
    const [selectedCsId, setSelectedCsId] = useState(linked.set);
    const linkedItem = selectedCsId && selectedCsId === linked.set ? linked.item : null;
    const selectedCs = changeSets.find((cs)=>cs.changeSetId === selectedCsId) ?? null;
    const changeSetLabel = (cs)=>cs.title || `${CHANGE_SET_TYPE_LABELS[cs.changeSetType] ?? cs.changeSetType} ${cs.changeSetId.slice(0, 8)}`;
    const rosterColumns = [
        {
            id: 'change',
            header: 'Change',
            cell: (cs)=>/*#__PURE__*/ _jsxs("span", {
                    className: "flex min-w-0 items-center gap-1.5",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "truncate font-medium",
                            children: changeSetLabel(cs)
                        }),
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: "neutral",
                            className: "shrink-0",
                            "data-testid": Testids.modificationCenter.changeSetTypeBadge,
                            "data-change-set-type": cs.changeSetType,
                            children: CHANGE_SET_TYPE_LABELS[cs.changeSetType] ?? cs.changeSetType
                        })
                    ]
                })
        },
        {
            id: 'state',
            header: 'Status',
            align: 'right',
            cell: (cs)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: RESOLUTION_BADGE_VARIANTS[cs.state] ?? 'neutral',
                    "data-testid": Testids.modificationCenter.resolutionStateBadge,
                    "data-resolution": cs.state,
                    children: cs.state
                })
        }
    ];
    return(// Stacked, not a master-detail split: the review's CLIN diff table needs the full
    // width, and a narrow master pane hid the roster's status and actions columns.
    /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        "data-testid": Testids.modificationCenter.workspaceRoot,
        children: [
            /*#__PURE__*/ _jsx(SpecraSurface, {
                radius: "lg",
                padding: "none",
                children: /*#__PURE__*/ _jsxs("div", {
                    "data-testid": Testids.modificationCenter.changeSetList,
                    children: [
                        /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                            title: "Contract Changes",
                            actions: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                                size: "sm",
                                label: "Show change sets",
                                value: view,
                                onValueChange: setView,
                                testId: Testids.modificationCenter.stateFilter,
                                options: [
                                    {
                                        value: 'active',
                                        label: 'Active',
                                        count: countIn('active')
                                    },
                                    {
                                        value: 'discarded',
                                        label: 'Discarded',
                                        count: countIn('discarded')
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraDataTable, {
                            rows: changeSets,
                            columns: rosterColumns,
                            getRowId: (cs)=>cs.changeSetId,
                            getRowActionLabel: (cs)=>`Review ${changeSetLabel(cs)}`,
                            onRowClick: (cs)=>setSelectedCsId(cs.changeSetId),
                            isRowSelected: (cs)=>cs.changeSetId === selectedCsId,
                            rowTestId: Testids.modificationCenter.changeSetRow,
                            getRowProps: (cs)=>({
                                    'data-change-set-id': cs.changeSetId
                                }),
                            loading: isLoading,
                            loadingLabel: "Loading…",
                            emptyTitle: view === 'discarded' ? 'No discarded change sets' : 'No contract changes yet',
                            emptyDescription: view === 'discarded' ? 'Change sets you discard, and ones a re-upload of the same document replaced, are listed here.' : 'Modifications, re-extraction proposals and contract intake questions appear here automatically once a modification document is processed, or a contract document is uploaded or re-uploaded, for this program.',
                            actionsLabel: "Document actions",
                            rowActions: (cs)=>{
                                const docId = cs.sourceArtifactIds?.[0];
                                const canDiscard = canEdit && DISCARDABLE_STATES.has(cs.state);
                                if (!docId && !canDiscard) return null;
                                return /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-0.5",
                                    children: [
                                        docId && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            label: `View ${cs.title || 'modification'} document`,
                                            title: "View source document",
                                            "data-testid": Testids.modificationCenter.changeSetViewDoc,
                                            onClick: ()=>setViewingDoc(cs),
                                            children: /*#__PURE__*/ _jsx(Eye, {})
                                        }),
                                        docId && canEdit && rosterView(cs.state) === 'active' && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            label: `Re-extract ${cs.title || 'modification'}`,
                                            title: "Re-extract modification",
                                            "data-testid": Testids.modificationCenter.changeSetReextract,
                                            disabled: reextractRunning,
                                            onClick: ()=>void reextract({
                                                    zouProjectId,
                                                    programId,
                                                    reextractDocId: docId,
                                                    documentKind: 'modification',
                                                    label: `Re-extracting ${cs.title || 'modification'}`,
                                                    link: `/delivery/programs/${programId}/workroom?tab=modifications`
                                                }),
                                            children: /*#__PURE__*/ _jsx(RefreshCw, {
                                                className: reextractRunning ? 'animate-spin' : undefined
                                            })
                                        }),
                                        canDiscard && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            variant: "ghost",
                                            size: "sm",
                                            label: `Discard ${changeSetLabel(cs)}`,
                                            title: "Discard this change set",
                                            "data-testid": Testids.modificationCenter.changeSetDiscard,
                                            onClick: ()=>setDiscarding(cs),
                                            children: /*#__PURE__*/ _jsx(Trash2, {})
                                        })
                                    ]
                                });
                            }
                        }),
                        discarding && /*#__PURE__*/ _jsx(DiscardChangeSetDialog, {
                            open: true,
                            onOpenChange: (next)=>{
                                if (!next) setDiscarding(null);
                            },
                            changeSetId: discarding.changeSetId,
                            label: changeSetLabel(discarding)
                        }),
                        viewingDoc && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                            isOpen: true,
                            onClose: ()=>setViewingDoc(null),
                            projectId: zouProjectId,
                            document: {
                                documentId: viewingDoc.sourceArtifactIds[0],
                                filename: viewingDoc.title || 'Modification'
                            }
                        })
                    ]
                })
            }),
            selectedCs ? /*#__PURE__*/ _jsx(ModificationReview, {
                cs: selectedCs,
                label: changeSetLabel(selectedCs),
                projectId: zouProjectId,
                programId: programId,
                openItemId: linkedItem
            }, selectedCs.changeSetId) : null
        ]
    }));
}
function ModificationReview({ cs, label, projectId, programId, openItemId }) {
    const changeSetId = cs.changeSetId;
    const isViewOnly = useIsViewOnly();
    const { data: summary, isLoading } = useChangeSetDiscrepancies(changeSetId);
    const commit = useCommitModification();
    const approveAll = useApproveAllDiscrepancies();
    const { user } = useAuth();
    const isAdmin = !!user?.role && ADMIN_ROLES.has(user.role);
    const [violations, setViolations] = useState([]);
    const [applyWarnings, setApplyWarnings] = useState([]);
    // A failed commit that names no items stays on the page: the toast fades.
    const [commitError, setCommitError] = useState('');
    const [viewerOpen, setViewerOpen] = useState(false);
    const sourceDocId = cs.sourceArtifactIds?.[0] ?? '';
    const canViewSource = !!sourceDocId && !!projectId;
    // An intake set names no document of its own; its review items quote the
    // award, so their passages open on the program's contract document.
    const { data: programDocs } = useProgramDocuments(!sourceDocId && cs.changeSetType === 'ContractIntake' ? programId : null);
    const reviewDocId = sourceDocId || contractDocumentId(programDocs?.items ?? []);
    const items = summary?.discrepancies ?? [];
    // Review items (D93) are pulled out of the CLIN and contract-level tables
    // into their own "Needs your call" group, which is the only place they
    // render (an identity item on a line has no target id to group under).
    const reviewItems = useMemo(()=>items.filter(isReviewItem), [
        items
    ]);
    const changeItems = useMemo(()=>items.filter((d)=>!isReviewItem(d)), [
        items
    ]);
    // SAD-887: a rule conflict is on a deliverable; it is named by its title,
    // read from the program's duties only when the set has one.
    // ContractDuty (award schedule) and DutyAssignment (SOW handling) both
    // name deliverables by duty key — load titles whenever either is present.
    const onDuty = reviewItems.some((d)=>d.review.targetObjectType === 'ContractDuty' || d.review.targetObjectType === 'DutyAssignment');
    const { data: dutyData } = useDuties(onDuty ? programId : '');
    const dutyNames = useMemo(()=>new Map((dutyData?.duties ?? []).map((d)=>[
                d.dutyKey,
                d.title
            ])), [
        dutyData
    ]);
    // Counted the way the list shows them: a group (the award's team names,
    // one conflict repeated line by line) is one call. Used only for Approve
    // all's tooltip — the commit gate uses itemsBlockingCommit (wire flags).
    const openReviewCount = useMemo(()=>groupReviewItems(reviewItems).filter(isOpenEntry).length, [
        reviewItems
    ]);
    // A period item's row reads the period's own name (cached, 30s staleTime;
    // the same query the item rows and the review panel use).
    const hasPeriodItem = reviewItems.some((d)=>targetKind(d.review.targetObjectType) === 'period');
    const { data: periodsResp } = popHooks.useList({
        programId
    }, {
        enabled: hasPeriodItem || itemsBlockingCommit(items).length > 0
    });
    const periods = periodsResp?.items ?? [];
    const [openReviewId, setOpenReviewId] = useState(openItemId ?? null);
    const [reviewOpen, setReviewOpen] = useState(!!openItemId);
    // The item a link asked for (?item=, a refusal's Open item): scrolled to,
    // opened and highlighted once the items are on the page. seq re-runs it
    // when the same item is asked for again.
    const [focus, setFocus] = useState(openItemId ? {
        id: openItemId,
        seq: 0
    } : null);
    const openItem = (id)=>{
        if (reviewItems.some((d)=>d.id === id)) {
            setOpenReviewId(id);
            setReviewOpen(true);
        }
        setFocus((f)=>({
                id,
                seq: (f?.seq ?? 0) + 1
            }));
    };
    const focusLoaded = !!focus && items.some((d)=>d.id === focus.id);
    useEffect(()=>{
        if (!focus || !focusLoaded) return;
        return highlightItemRow(focus.id);
    }, [
        focus,
        focusLoaded
    ]);
    const [refusals, setRefusals] = useState([]);
    // Same population zou's validateCommitInvariants refuses on — open/routed,
    // unresolved Blocking, no-writer still needing Leave blank. Never a second
    // heuristic (SAD-901).
    const blockingItems = useMemo(()=>itemsBlockingCommit(items), [
        items
    ]);
    const unsettledIds = blockingItems.map((d)=>d.id);
    const unresolvedBlocking = blockingItems.filter((d)=>d.severity === 'Blocking').length;
    // Approve all never touches a review item: accepting one with no chosen
    // value writes nothing (D86/D87), so each needs its own call. Nor an item
    // no writer lands (SAD-700): accepting it would refuse the whole apply, so
    // it is left for the reviewer to leave blank, and the count says so.
    const noWriterUnsettled = items.filter((d)=>noWriterNeedsDecision(d)).length;
    const approvableIds = unsettledIds.filter((id)=>{
        const d = items.find((x)=>x.id === id);
        return !!d && !isReviewItem(d) && !hasNoWriter(d);
    });
    const ready = items.length > 0 && unsettledIds.length === 0;
    // An Applied set has nothing left to apply; zou's commit gate refuses it
    // again, so the button says so instead of offering it.
    const applied = cs.state === 'Applied';
    const discarded = cs.state === 'Cancelled';
    const canDiscard = !isViewOnly && DISCARDABLE_STATES.has(cs.state);
    const [discardOpen, setDiscardOpen] = useState(false);
    // SAD-768: a reviewer adds or corrects a line only on an open intake or
    // modification set; zou refuses every other set type.
    const canStageLines = !isViewOnly && !applied && !discarded && (cs.changeSetType === 'ContractIntake' || cs.changeSetType === 'Modification');
    // A ReExtraction set's control applies re-extraction-proposed contact/company
    // changes, not a contract modification — "Commit Modification" would mislead
    // a reviewer on that set. Reuses CHANGE_SET_TYPE_LABELS rather than a second
    // type→label switch.
    const commitButtonLabel = cs.changeSetType === 'ReExtraction' ? `Apply ${CHANGE_SET_TYPE_LABELS.ReExtraction.toLowerCase()} changes` : cs.changeSetType === 'ContractIntake' ? 'Apply intake decisions' : 'Commit Modification';
    const commitBlockedTitle = needsDecisionNote(unsettledIds.length);
    const onApproveAll = ()=>{
        approveAll.mutate({
            changeSetId,
            discrepancyIds: approvableIds,
            decisionRationale: 'Bulk approved by admin'
        });
    };
    const onCommit = async ()=>{
        setViolations([]);
        setApplyWarnings([]);
        setRefusals([]);
        setCommitError('');
        let resp;
        try {
            resp = await commit.mutateAsync({
                changeSetId
            });
        } catch (err) {
            // A refused apply names its items (listed below, each linking to its
            // item); useCommitModification's onError toasts. Rethrowing would only
            // surface it as an unhandled rejection.
            const named = commitRefusals(err);
            if (named.length > 0) setRefusals(named);
            else setCommitError(apiErrorMessage(err, 'Failed to commit modification'));
            return;
        }
        if (resp.violations.length > 0) {
            setViolations(resp.violations);
            return;
        }
        const s = resp.summary;
        // Surface per-item skip reasons in-DOM, not just the count — a skipped item
        // must explain itself (a toast auto-dismisses; the reasons persist here).
        setApplyWarnings(s?.applyWarnings ?? []);
        const appliedLabel = CHANGE_SET_TYPE_LABELS[cs.changeSetType] ?? 'Modification';
        toast.success(`${appliedLabel} applied — ${s?.appliedCount ?? 0} written, ${s?.skippedCount ?? 0} skipped`);
    };
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx(SpecraPanelHeader, {
                title: `Change items${isLoading ? '' : ` (${items.length})`}`,
                actions: /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-wrap items-center gap-3 text-xs text-muted-foreground",
                    children: [
                        applied && /*#__PURE__*/ _jsx(SpecraText, {
                            size: "caption",
                            tone: "muted",
                            "data-testid": Testids.modificationCenter.commitAppliedNote,
                            children: ALREADY_APPLIED_NOTE
                        }),
                        discarded && /*#__PURE__*/ _jsx(SpecraText, {
                            size: "caption",
                            tone: "muted",
                            title: cs.updatedBy || undefined,
                            "data-testid": Testids.modificationCenter.discardedNote,
                            children: discardedNote(cs)
                        }),
                        !applied && !discarded && !ready && items.length > 0 && /*#__PURE__*/ _jsxs(SpecraText, {
                            size: "caption",
                            tone: "warning",
                            className: "font-medium",
                            "data-testid": Testids.modificationCenter.commitBlockedReason,
                            "data-count": unsettledIds.length,
                            children: [
                                needsDecisionNote(unsettledIds.length),
                                unresolvedBlocking > 0 ? ` (${unresolvedBlocking} Blocking)` : '',
                                ".",
                                ' ',
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    type: "button",
                                    variant: "link",
                                    size: "sm",
                                    className: "h-auto p-0 align-baseline text-[length:var(--fs-ui-caption)]",
                                    onClick: scrollToBlockingList,
                                    "data-testid": Testids.reviewItem.blockingListLink,
                                    children: "See the list"
                                })
                            ]
                        }),
                        canViewSource && /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "secondary",
                            onClick: ()=>setViewerOpen(true),
                            "data-testid": Testids.modificationCenter.viewSourceButton,
                            children: "View source document"
                        }),
                        isAdmin && !discarded && approvableIds.length > 0 && /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                noWriterUnsettled > 0 && /*#__PURE__*/ _jsx(SpecraText, {
                                    size: "caption",
                                    tone: "muted",
                                    "data-testid": Testids.modificationCenter.approveAllSkippedNote,
                                    "data-skipped": noWriterUnsettled,
                                    children: approveAllSkippedNote(noWriterUnsettled)
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    disabled: approveAll.isPending,
                                    onClick: onApproveAll,
                                    "data-testid": Testids.modificationCenter.approveAllButton,
                                    "data-count": approvableIds.length,
                                    "data-skipped": noWriterUnsettled,
                                    title: approveAllTitle(openReviewCount, noWriterUnsettled),
                                    children: approveAll.isPending ? 'Approving…' : `Approve all (${approvableIds.length}${noWriterUnsettled > 0 ? `, ${noWriterUnsettled} skipped` : ''})`
                                })
                            ]
                        }),
                        canDiscard && /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            variant: "destructive",
                            onClick: ()=>setDiscardOpen(true),
                            "data-testid": Testids.modificationCenter.discardButton,
                            children: "Discard"
                        }),
                        !discarded && /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            disabled: applied || !ready || commit.isPending || isViewOnly,
                            onClick: onCommit,
                            "data-testid": Testids.modificationCenter.commitButton,
                            title: applied ? ALREADY_APPLIED_NOTE : ready ? undefined : commitBlockedTitle,
                            children: commit.isPending ? 'Applying…' : commitButtonLabel
                        })
                    ]
                })
            }),
            discardOpen && /*#__PURE__*/ _jsx(DiscardChangeSetDialog, {
                open: true,
                onOpenChange: setDiscardOpen,
                changeSetId: changeSetId,
                label: label
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-2 p-3 text-sm sm:p-4",
                children: [
                    !isLoading && blockingItems.length > 0 && /*#__PURE__*/ _jsx(BlockingCommitList, {
                        items: items,
                        periods: periods,
                        targetNames: dutyNames,
                        onOpen: (id)=>{
                            setOpenReviewId(id);
                            setReviewOpen(true);
                        }
                    }),
                    /*#__PURE__*/ _jsx(CommitRefusalPanel, {
                        refusals: refusals,
                        changeSetId: changeSetId,
                        onOpen: openItem
                    }),
                    commitError && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "danger",
                        variant: "framed",
                        title: "Not applied:",
                        "data-testid": Testids.modificationCenter.commitError,
                        children: commitError
                    }),
                    violations.length > 0 && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "danger",
                        variant: "framed",
                        title: "Commit blocked:",
                        children: /*#__PURE__*/ _jsx("ul", {
                            className: "list-disc pl-5",
                            children: violations.map((v, i)=>/*#__PURE__*/ _jsx("li", {
                                    children: v.detail || v.kind
                                }, `${v.kind}-${i}`))
                        })
                    }),
                    applyWarnings.length > 0 && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                        tone: "warning",
                        variant: "framed",
                        title: "Applied with skipped items:",
                        "data-testid": Testids.modificationCenter.applyWarnings,
                        children: /*#__PURE__*/ _jsx("ul", {
                            className: "list-disc pl-5",
                            children: applyWarnings.map((w, i)=>/*#__PURE__*/ _jsx("li", {
                                    children: w
                                }, `warn-${i}`))
                        })
                    }),
                    isLoading && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "Loading…"
                    }),
                    !isLoading && items.length === 0 && /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "No change items on this modification."
                    }),
                    !isLoading && (reviewItems.length > 0 || canStageLines) && /*#__PURE__*/ _jsx(ReviewItemList, {
                        items: reviewItems,
                        periods: periods,
                        onOpen: (id)=>{
                            setOpenReviewId(id);
                            setReviewOpen(true);
                        },
                        allowAddLine: canStageLines,
                        programId: programId,
                        changeSetId: changeSetId,
                        targetNames: dutyNames
                    }),
                    !isLoading && changeItems.length > 0 && /*#__PURE__*/ _jsx(ClinDiffTable, {
                        items: changeItems,
                        programId: programId,
                        projectId: projectId,
                        changeSetId: changeSetId,
                        sourceDocId: sourceDocId,
                        allowCorrectLine: canStageLines,
                        focusItemId: focus?.id ?? null
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ReviewItemDrawer, {
                items: reviewItems,
                openId: openReviewId,
                open: reviewOpen,
                onClose: ()=>setReviewOpen(false),
                programId: programId,
                projectId: projectId,
                changeSetId: changeSetId,
                setOpen: DISCARDABLE_STATES.has(cs.state),
                targetNames: dutyNames,
                fallbackDocId: reviewDocId,
                fallbackDocLabel: sourceDocId ? cs.title || CHANGE_SET_TYPE_LABELS[cs.changeSetType] || 'Source document' : 'Contract'
            }),
            canViewSource && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: viewerOpen,
                onClose: ()=>setViewerOpen(false),
                projectId: projectId,
                document: {
                    documentId: sourceDocId,
                    filename: cs.title || 'Modification'
                }
            })
        ]
    });
}
function buildClinDiffRows(items, currentByClin) {
    const out = [];
    for (const g of groupByTarget(items)){
        // Only CLIN/ContractCLIN targets get a CLIN row — a Contact, Company, or
        // period item can carry a target id too (its email / company name / own
        // natural key), so id presence alone can't gate this.
        if (!g.id || !CLIN_RECORD_TYPES.has(g.type)) continue;
        const current = currentByClin.get(g.id) ?? null;
        const addItem = g.items.find(isObjectAdd);
        if (addItem) {
            out.push({
                clin: g.id,
                op: 'Added',
                added: parseNewClin(addItem),
                current,
                changed: new Map(),
                items: g.items
            });
            continue;
        }
        const changed = new Map();
        const ops = new Set(g.items.map(changeOp));
        for (const it of g.items){
            changed.set(targetFieldPath(it), {
                before: fmtVal(it.baselineValueJson, targetFieldPath(it)),
                after: fmtVal(it.proposedValueJson, targetFieldPath(it))
            });
        }
        // One kind of change across the line's items names the row; a mix is a change.
        const [only] = ops;
        out.push({
            clin: g.id,
            op: ops.size === 1 && only !== 'Added' ? only : 'Changed',
            added: null,
            current,
            changed,
            items: g.items
        });
    }
    return out;
}
// A before→after cell: red-strike old, green new.
function DiffPair({ before, after, money }) {
    const b = money ? moneyStrOrDash(before) : before;
    const a = money ? moneyStrOrDash(after) : after;
    return /*#__PURE__*/ _jsxs("span", {
        className: "inline-flex flex-wrap items-center gap-1",
        children: [
            /*#__PURE__*/ _jsx("span", {
                className: "rounded bg-danger-muted px-1 text-danger line-through",
                children: b
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "text-muted-foreground",
                children: "→"
            }),
            /*#__PURE__*/ _jsx("span", {
                className: "rounded bg-success-muted px-1 font-medium text-success",
                children: a
            })
        ]
    });
}
// Extraction confidence for a change item — the value already on the payload
// (source_after/before_confidence, 0-1). SAD-115: matt reported the % never
// showed on the review row, only inside the source-chip popover. A row with
// no confidence (a passthrough/derived item) shows none, never a misleading 0%.
function rowConfidence(d) {
    return d.sourceAfterConfidence || d.sourceBeforeConfidence || 0;
}
// Side-by-side source comparison — the proposal-center mechanic, not a custom
// viewer: the modification document opens as a standalone DocumentViewerDrawer
// (right, 60vw) and the current-value document opens in extension mode,
// portaled beside it — both land on the ref's page with the block highlighted.
function CompareSourceDrawers({ d, projectId, onClose }) {
    const after = parseSpan(d.sourceAfterSpan);
    const before = parseSpan(d.sourceBeforeSpan);
    // Extension mode positions via `right: parentDrawerWidth px` — match the
    // standalone drawer's responsive width (60vw desktop / 70vw small).
    const parentWidth = Math.round(window.innerWidth * (window.innerWidth >= 1024 ? 0.6 : 0.7));
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            d.sourceAfterArtifactId && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: true,
                onClose: onClose,
                projectId: projectId,
                document: {
                    documentId: d.sourceAfterArtifactId,
                    filename: 'This modification'
                },
                highlightBlockId: after.blockId,
                initialPage: d.sourceAfterPage > 0 ? d.sourceAfterPage : after.page ?? 1
            }),
            d.sourceBeforeArtifactId && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: true,
                onClose: onClose,
                projectId: projectId,
                document: {
                    documentId: d.sourceBeforeArtifactId,
                    filename: 'Current contract'
                },
                highlightBlockId: before.blockId,
                initialPage: d.sourceBeforePage > 0 ? d.sourceBeforePage : before.page ?? 1,
                // Extension mode positions itself beside the standalone drawer — with
                // no after-doc there is no parent to sit beside, so render standalone.
                mode: d.sourceAfterArtifactId ? 'extension' : 'standalone',
                parentDrawerWidth: parentWidth
            })
        ]
    });
}
// A single-open accordion over a table's controlled expansion: the id the
// toggle just added wins, a toggle that only removes closes everything.
function nextOpenId(ids, current) {
    const added = ids.find((id)=>String(id) !== current);
    return added === undefined ? null : String(added);
}
export function ClinDiffTable({ items, programId, projectId, changeSetId, sourceDocId, allowCorrectLine = false, focusItemId = null }) {
    const { data: clinsResp } = useContractCLINs(programId);
    const currentByClin = useMemo(()=>{
        const m = new Map();
        for (const c of clinsResp?.clins ?? [])m.set(c.clinNumber, c);
        return m;
    }, [
        clinsResp
    ]);
    // Review items render in their own group (ReviewItemList), never here.
    const tableItems = useMemo(()=>items.filter((d)=>!isReviewItem(d)), [
        items
    ]);
    const rows = useMemo(()=>buildClinDiffRows(tableItems, currentByClin), [
        tableItems,
        currentByClin
    ]);
    const [openClin, setOpenClin] = useState(null);
    const [openItemId, setOpenItemId] = useState(null);
    useEffect(()=>{
        if (!focusItemId) return;
        const row = rows.find((r)=>r.items.some((d)=>d.id === focusItemId));
        if (row) setOpenClin(row.clin);
        if (tableItems.some((d)=>d.id === focusItemId)) setOpenItemId(focusItemId);
    }, [
        focusItemId,
        rows,
        tableItems
    ]);
    const [comparing, setComparing] = useState(null);
    const [viewingSourceDoc, setViewingSourceDoc] = useState(false);
    const [correctClin, setCorrectClin] = useState(null);
    const onViewDoc = sourceDocId ? ()=>setViewingSourceDoc(true) : undefined;
    const drawers = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            comparing && /*#__PURE__*/ _jsx(CompareSourceDrawers, {
                d: comparing,
                projectId: projectId,
                onClose: ()=>setComparing(null)
            }),
            viewingSourceDoc && sourceDocId && /*#__PURE__*/ _jsx(DocumentViewerDrawer, {
                isOpen: true,
                onClose: ()=>setViewingSourceDoc(false),
                projectId: projectId,
                document: {
                    documentId: sourceDocId,
                    filename: 'Modification'
                }
            })
        ]
    });
    const nonClin = /*#__PURE__*/ _jsx(NonClinItems, {
        items: tableItems,
        programId: programId,
        projectId: projectId,
        openItemId: openItemId,
        setOpenItemId: setOpenItemId,
        onCompare: setComparing
    });
    if (rows.length === 0) {
        // A mod can be purely contract-level (period extension, admin change) —
        // the non-CLIN section must still render or those items are invisible
        // to reviewers and get blind-approved.
        return /*#__PURE__*/ _jsxs("div", {
            className: "rounded-md border",
            "data-testid": Testids.modificationCenter.itemTable,
            children: [
                /*#__PURE__*/ _jsx("p", {
                    className: "px-3 py-2 text-muted-foreground",
                    children: "No CLIN-level changes on this modification."
                }),
                drawers,
                nonClin
            ]
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "rounded-md border",
        "data-testid": Testids.modificationCenter.itemTable,
        children: [
            /*#__PURE__*/ _jsx(SpecraDataTable, {
                rows: rows,
                columns: clinDiffColumns(onViewDoc),
                getRowId: (r)=>r.clin,
                rowTestId: Testids.modificationCenter.clinGroup,
                // The resolved cell values ride on the row as data-* attributes, the
                // same way periodDiff/deliverableDiff carry data-before/data-after.
                // SAD-115: the modification-clin-row-fidelity journey asserts row-level
                // invariants (a SLIN's Type matches its parent CLIN's, an Amount is
                // never shown without a Funded $, a Title is never a placeholder) and
                // reading them here beats parsing "$3,545,332.00" back out of rendered
                // text or depending on <td> ordering.
                getRowProps: (r)=>{
                    const v = clinRowView(r);
                    return {
                        className: 'cursor-pointer',
                        'data-object-id': r.clin,
                        'data-op': r.op,
                        'data-parent-clin': r.added?.parent_clin_number || r.current?.parentClinNumber || '',
                        'data-title': v.title,
                        'data-type': v.type,
                        'data-qty': v.qty,
                        'data-amount': v.amount,
                        'data-funded': r.op === 'Added' ? moneyOrDash(r.added?.funded_amount) : '',
                        onClick: ()=>setOpenClin((cur)=>cur === r.clin ? null : r.clin)
                    };
                },
                getRowTone: (r)=>r.op === 'Added' ? 'success' : r.op === 'Removed' ? 'danger' : undefined,
                expandedIds: openClin ? [
                    openClin
                ] : [],
                onExpandedChange: (ids)=>setOpenClin(nextOpenId(ids, openClin)),
                getExpandToggleLabel: (r, expanded)=>`${expanded ? 'Hide' : 'Show'} the change items for CLIN ${r.clin}`,
                getExpandedRowProps: (r)=>({
                        'data-object-id': r.clin
                    }),
                renderExpanded: (r)=>/*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            allowCorrectLine && r.op !== 'Added' && /*#__PURE__*/ _jsx("div", {
                                className: "flex justify-end px-1",
                                children: /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    "data-testid": Testids.reviewItem.correctThisLine,
                                    "data-clin-number": r.clin,
                                    onClick: (e)=>{
                                        e.stopPropagation();
                                        setCorrectClin({
                                            clin: r.clin,
                                            title: r.current?.shortTitle || ''
                                        });
                                    },
                                    children: "Correct this line"
                                })
                            }),
                            /*#__PURE__*/ _jsx(ChangeItems, {
                                items: r.items,
                                programId: programId,
                                projectId: projectId,
                                changeSetId: changeSetId,
                                openItemId: openItemId,
                                setOpenItemId: setOpenItemId,
                                onCompare: setComparing
                            })
                        ]
                    })
            }),
            drawers,
            nonClin,
            allowCorrectLine && /*#__PURE__*/ _jsx(StageContractLineDrawer, {
                open: !!correctClin,
                onOpenChange: (next)=>{
                    if (!next) setCorrectClin(null);
                },
                mode: "correct",
                programId: programId,
                changeSetId: changeSetId,
                initialClinNumber: correctClin?.clin ?? '',
                initialTitle: correctClin?.title ?? ''
            })
        ]
    });
}
// Everything that is not a contract line: Contact, Company/
// ProgramCompanyAssignment, PeriodOfPerformance, optional tasks, and any other
// target type without a CLIN-table row to live under. Without this section
// these items were invisible in the review and got blind-approved by
// approve-all. Renders the same item rows (dispositions + source comparison).
function NonClinItems({ items, programId, projectId, openItemId, setOpenItemId, onCompare }) {
    // A line item with no target id has no CLIN row to live under
    // (buildClinDiffRows skips it), so it renders here rather than nowhere.
    const rest = items.filter((d)=>!isClinTarget(d) || !targetObjectId(d));
    if (rest.length === 0) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "border-t px-3 py-2",
        "data-testid": Testids.modificationCenter.nonClinSection,
        children: [
            /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                variant: "group",
                className: "mb-1",
                title: "Contract-level changes (contacts, companies, periods, dates, admin)"
            }),
            /*#__PURE__*/ _jsx(ChangeItems, {
                items: rest,
                programId: programId,
                projectId: projectId,
                openItemId: openItemId,
                setOpenItemId: setOpenItemId,
                onCompare: onCompare
            })
        ]
    });
}
function clinRowView(r) {
    const a = r.added;
    const c = r.current;
    return {
        title: a?.title || c?.shortTitle || c?.description || '—',
        type: a?.contract_type || c?.contractType || '—',
        qty: a?.quantity || (c?.quantity ? String(c.quantity) : '') || '—',
        amount: a ? moneyOrDash(a.extended_price) : moneyOrDash(c?.pricingExtendedPrice),
        strike: r.op === 'Removed' ? 'text-danger line-through' : ''
    };
}
function clinDiffColumns(onViewDoc) {
    return [
        {
            id: 'clin',
            header: 'CLIN',
            cell: (r)=>/*#__PURE__*/ _jsxs("span", {
                    className: "inline-flex items-center gap-2 font-mono",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraBadge, {
                            variant: OP_BADGE_VARIANTS[r.op],
                            "data-testid": Testids.modificationCenter.changeOpBadge,
                            "data-op": r.op,
                            children: r.op
                        }),
                        /*#__PURE__*/ _jsx("span", {
                            className: clinRowView(r).strike,
                            children: r.clin
                        }),
                        onViewDoc && /*#__PURE__*/ _jsx(SpecraIconButton, {
                            variant: "ghost",
                            size: "sm",
                            label: `View source document for CLIN ${r.clin}`,
                            title: "View the modification document",
                            "data-testid": Testids.modificationCenter.clinViewDoc,
                            onClick: (e)=>{
                                e.stopPropagation();
                                onViewDoc();
                            },
                            children: /*#__PURE__*/ _jsx(Eye, {})
                        })
                    ]
                })
        },
        {
            id: 'title',
            header: 'Title',
            className: 'max-w-xs',
            cell: (r)=>{
                const v = clinRowView(r);
                return /*#__PURE__*/ _jsx("span", {
                    className: `block truncate ${v.strike}`,
                    title: v.title,
                    children: v.title
                });
            }
        },
        {
            id: 'type',
            header: 'Type',
            cell: (r)=>{
                const v = clinRowView(r);
                return /*#__PURE__*/ _jsx("span", {
                    className: v.strike,
                    children: v.type
                });
            }
        },
        {
            id: 'qty',
            header: 'Qty',
            align: 'right',
            cell: (r)=>{
                const v = clinRowView(r);
                return /*#__PURE__*/ _jsx("span", {
                    className: v.strike,
                    children: v.qty
                });
            }
        },
        {
            id: 'amount',
            header: 'Amount',
            align: 'right',
            cell: (r)=>{
                const v = clinRowView(r);
                return /*#__PURE__*/ _jsx("span", {
                    className: v.strike,
                    children: v.amount
                });
            }
        },
        {
            id: 'funded',
            header: 'Funded $',
            align: 'right',
            cell: (r)=>{
                const fundedChange = r.changed.get('funded_amount');
                if (r.op === 'Added') {
                    return /*#__PURE__*/ _jsx("span", {
                        className: "rounded bg-success-muted px-1 font-medium text-success",
                        children: moneyOrDash(r.added?.funded_amount)
                    });
                }
                if (fundedChange) {
                    return /*#__PURE__*/ _jsx(DiffPair, {
                        before: fundedChange.before,
                        after: fundedChange.after,
                        money: true
                    });
                }
                return /*#__PURE__*/ _jsx("span", {
                    className: clinRowView(r).strike,
                    children: moneyOrDash(r.current?.pricingFundedAmount)
                });
            }
        },
        {
            id: 'pop',
            header: 'PoP',
            className: 'text-xs',
            cell: (r)=>{
                const a = r.added;
                const c = r.current;
                const popChange = r.changed.get('pop_end_date');
                if (r.op === 'Added') {
                    return /*#__PURE__*/ _jsxs("span", {
                        className: "text-success",
                        children: [
                            a?.pop_start_date || '—',
                            " → ",
                            a?.pop_end_date || '—'
                        ]
                    });
                }
                if (popChange) return /*#__PURE__*/ _jsx(DiffPair, {
                    before: popChange.before,
                    after: popChange.after
                });
                return /*#__PURE__*/ _jsx("span", {
                    className: clinRowView(r).strike,
                    children: c?.popEndDate ? `${c?.popStartDate || '—'} → ${c.popEndDate}` : '—'
                });
            }
        },
        {
            id: 'confidence',
            header: 'Conf.',
            align: 'right',
            cell: (r)=>{
                const confidence = Math.max(0, ...r.items.map(rowConfidence));
                return confidence > 0 ? /*#__PURE__*/ _jsx(ItemConfidence, {
                    confidence: confidence
                }) : null;
            }
        }
    ];
}
function ItemConfidence({ confidence }) {
    return /*#__PURE__*/ _jsxs(SpecraBadge, {
        variant: "neutral",
        "data-testid": Testids.modificationCenter.confidence,
        "data-confidence": confidence,
        title: "Extraction confidence",
        children: [
            Math.round(confidence * 100),
            "%"
        ]
    });
}
// One table of change items: a summary row per item, the resolve form in its
// detail row. One item is open at a time across every table on the review
// (openItemId is shared), so there is only ever one decision form on the page.
function ChangeItems({ items, programId, projectId, changeSetId, openItemId, setOpenItemId, onCompare }) {
    return /*#__PURE__*/ _jsx(SpecraDataTable, {
        rows: [
            ...items
        ],
        columns: [
            {
                id: 'change',
                header: 'Change',
                cell: (d)=>/*#__PURE__*/ _jsx(ItemSummary, {
                        d: d,
                        programId: programId,
                        projectId: projectId,
                        onCompare: onCompare
                    })
            },
            {
                id: 'severity',
                header: 'Severity',
                cell: (d)=>/*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: SEVERITY_BADGE_VARIANTS[d.severity] ?? 'neutral',
                        "data-testid": Testids.modificationCenter.severityBadge,
                        "data-severity": d.severity,
                        children: d.severity
                    })
            },
            {
                id: 'owner',
                header: 'Owner',
                cell: (d)=>/*#__PURE__*/ _jsx(SpecraText, {
                        size: "caption",
                        tone: "muted",
                        children: d.functionalOwner
                    })
            },
            {
                id: 'status',
                header: 'Status',
                align: 'right',
                cell: (d)=>/*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraBadge, {
                                variant: RESOLUTION_BADGE_VARIANTS[d.resolutionState] ?? 'neutral',
                                "data-testid": Testids.modificationCenter.resolutionStateBadge,
                                "data-resolution": d.resolutionState,
                                children: resolutionLabel(d.resolutionState, d.review)
                            }),
                            READY_TO_APPLY.has(d.resolutionState) && /*#__PURE__*/ _jsxs(SpecraText, {
                                as: "div",
                                size: "micro",
                                tone: "warning",
                                className: "mt-0.5",
                                "data-testid": Testids.modificationCenter.itemStagedNote,
                                children: [
                                    d.resolutionState,
                                    " — applies on commit"
                                ]
                            })
                        ]
                    })
            }
        ],
        getRowId: (d)=>d.id,
        rowTestId: Testids.modificationCenter.itemRow,
        getRowProps: (d)=>({
                className: 'cursor-pointer',
                'data-item-id': d.id,
                'data-period-key': isPeriodTarget(d) ? targetObjectId(d) : undefined,
                onClick: ()=>setOpenItemId((cur)=>cur === d.id ? null : d.id)
            }),
        expandedIds: openItemId ? [
            openItemId
        ] : [],
        onExpandedChange: (ids)=>setOpenItemId(nextOpenId(ids, openItemId)),
        getExpandToggleLabel: (d, expanded)=>`${expanded ? 'Close' : 'Open'} the decision for ${fieldLabel(d)}`,
        getExpandedRowProps: (d)=>({
                'data-item-id': d.id
            }),
        renderExpanded: (d)=>/*#__PURE__*/ _jsx(ItemDetail, {
                d: d,
                programId: programId,
                changeSetId: changeSetId ?? d.changeSetId
            })
    });
}
// The item's first cell: what changes, its before→after, and the source links.
function ItemSummary({ d, programId, projectId, onCompare }) {
    const op = changeOp(d);
    const confidence = rowConfidence(d);
    // A period item reads as "<period name> · <field>" with a real before→after,
    // instead of the bare field name every other contract-level row shows — the
    // period is the thing being changed, and its natural key is the only way the
    // reviewer can tell which one.
    const isPeriod = isPeriodTarget(d);
    // A deliverable (ProgramTask) item reads the same way — "<deliverable
    // label> · <field>" with a real before→after — so the reviewer sees which
    // CDRL is targeted without leaving the review table.
    const isDeliverable = isDeliverableTarget(d);
    // An OptionalTask item reads the same way — "<task label> · <field>" — so
    // seven "Exercised -> true" rows from one modification are each attributed
    // to their own task instead of reading as identical anonymous rows.
    const isOptionalTask = isOptionalTaskTarget(d);
    const key = targetObjectId(d);
    const field = targetFieldPath(d);
    const before = fmtVal(d.baselineValueJson, field);
    const after = fmtVal(d.proposedValueJson, field);
    // Hooks can't be conditional, so this always runs — it's cheap/cached
    // (30s staleTime) and shared with the item's decision form.
    const { data: periodsResp } = popHooks.useList({
        programId
    }, {
        enabled: isPeriod
    });
    // Once a reviewer stamps a real period row id onto a previously-targetless
    // item (SetDiscrepancyTargetPeriod), the key stops being a natural key
    // ("base"/"option:N") periodLabel recognizes — prefer the loaded period's
    // own name when we have one.
    const matchedPeriod = isPeriod ? (periodsResp?.items ?? []).find((p)=>p.id === key) : undefined;
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        variant: OP_BADGE_VARIANTS[op],
                        "data-testid": Testids.modificationCenter.changeOpBadge,
                        "data-op": op,
                        children: op
                    }),
                    isPeriod ? /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-baseline gap-1.5",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                "data-testid": Testids.modificationCenter.periodName,
                                "data-period-key": key,
                                children: matchedPeriod ? matchedPeriod.periodName : periodLabel(key)
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-muted-foreground",
                                children: field ? humanizeField(field) : 'Period'
                            })
                        ]
                    }) : isDeliverable ? /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-baseline gap-1.5",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                "data-testid": Testids.modificationCenter.deliverableName,
                                "data-deliverable-key": key,
                                children: deliverableLabel(key)
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-muted-foreground",
                                children: field ? humanizeField(field) : 'Deliverable'
                            })
                        ]
                    }) : isOptionalTask ? /*#__PURE__*/ _jsxs("span", {
                        className: "flex items-baseline gap-1.5",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-medium",
                                "data-testid": Testids.modificationCenter.optionalTaskName,
                                "data-optional-task-key": key,
                                children: optionalTaskLabel(key)
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs text-muted-foreground",
                                children: field ? humanizeField(field) : 'Optional Task'
                            })
                        ]
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: "font-medium",
                        children: fieldLabel(d)
                    }),
                    (d.sourceBeforeArtifactId || d.sourceAfterArtifactId) && /*#__PURE__*/ _jsx(SpecraButton, {
                        variant: "secondary",
                        size: "sm",
                        title: "Compare the source pages side by side (current contract vs this modification)",
                        "data-testid": Testids.modificationCenter.compareButton,
                        onClick: (e)=>{
                            e.stopPropagation();
                            onCompare(d);
                        },
                        children: "Compare source"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        onClick: (e)=>e.stopPropagation(),
                        children: /*#__PURE__*/ _jsx(ReviewSourceChip, {
                            projectId: projectId,
                            ...reviewSourceProps(d, {
                                after: 'This modification',
                                before: 'Current contract'
                            })
                        })
                    }),
                    confidence > 0 && /*#__PURE__*/ _jsx(ItemConfidence, {
                        confidence: confidence
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mt-1 flex flex-wrap items-center gap-1.5 text-xs",
                children: isPeriod ? /*#__PURE__*/ _jsx("span", {
                    "data-testid": Testids.modificationCenter.periodDiff,
                    "data-period-key": key,
                    "data-period-field": field,
                    "data-before": before,
                    "data-after": after,
                    children: /*#__PURE__*/ _jsx(DiffPair, {
                        before: before,
                        after: after,
                        money: PERIOD_MONEY_FIELDS.has(field)
                    })
                }) : isDeliverable ? /*#__PURE__*/ _jsx("span", {
                    "data-testid": Testids.modificationCenter.deliverableDiff,
                    "data-deliverable-key": key,
                    "data-deliverable-field": field,
                    "data-before": before,
                    "data-after": after,
                    children: /*#__PURE__*/ _jsx(DiffPair, {
                        before: before,
                        after: after
                    })
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        op !== 'Added' && /*#__PURE__*/ _jsx("span", {
                            className: "rounded bg-muted px-1.5 py-0.5 text-muted-foreground line-through",
                            children: before
                        }),
                        op === 'Changed' && /*#__PURE__*/ _jsx("span", {
                            className: "text-muted-foreground",
                            children: "→"
                        }),
                        op !== 'Removed' && /*#__PURE__*/ _jsx("span", {
                            className: "rounded bg-success-muted px-1.5 py-0.5 font-medium text-success",
                            children: isObjectAdd(d) ? addSummary(d) : after
                        })
                    ]
                })
            }),
            noWriterNeedsDecision(d) && /*#__PURE__*/ _jsx(SpecraText, {
                as: "div",
                size: "meta",
                tone: "warning",
                className: "mt-1",
                "data-testid": Testids.modificationCenter.noWriterNote,
                "data-item-id": d.id,
                children: NO_WRITER_NOTE
            })
        ]
    });
}
// The item's detail row: the new line's facts, the calendar impact, and the
// resolve / route controls.
function ItemDetail({ d, programId, changeSetId }) {
    const isViewOnly = useIsViewOnly();
    const resolve = useResolveDiscrepancy();
    const route = useRouteDiscrepancy();
    const setTarget = useSetDiscrepancyTargetPeriod();
    // An item no writer lands offers Leave blank alone (SAD-700); the wire can
    // change under the form (a targetless period item gains a target), so the
    // chosen decision is read against what is offered now, never stored stale.
    const decisions = offeredDecisions(d, RESOLUTION_DECISIONS);
    const [chosenDecision, setDecision] = useState('Accepted');
    const decision = decisions.includes(chosenDecision) ? chosenDecision : decisions[0];
    const [rationale, setRationale] = useState('');
    const [correctedValue, setCorrectedValue] = useState('');
    const [owner, setOwner] = useState(d.functionalOwner || 'Delivery');
    const [targetPeriodId, setTargetPeriodId] = useState('');
    const isPeriod = isPeriodTarget(d);
    const isDeliverable = isDeliverableTarget(d);
    const key = targetObjectId(d);
    const field = targetFieldPath(d);
    const calendarImpact = isDeliverable ? DELIVERABLE_CHANGE_UNWRITTEN : null;
    const { data: periodsResp } = popHooks.useList({
        programId
    }, {
        enabled: isPeriod
    });
    const periods = periodsResp?.items ?? [];
    const correctedInputType = correctedFieldKind(field);
    const serializedCorrected = correctedValueJson(field, correctedValue);
    const nc = parseNewClin(d);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            nc && /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 space-y-2 rounded border bg-background p-2.5 text-xs",
                "data-testid": Testids.modificationCenter.newClinDetail,
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "font-medium text-foreground",
                        children: [
                            "New line item ",
                            targetObjectId(d),
                            nc.title ? ` — ${nc.title}` : ''
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-x-4 gap-y-1",
                        children: [
                            nc.contract_type && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Type:"
                                    }),
                                    " ",
                                    nc.contract_type
                                ]
                            }),
                            nc.parent_clin_number && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Parent CLIN:"
                                    }),
                                    " ",
                                    nc.parent_clin_number
                                ]
                            }),
                            nc.is_optional_task && nc.optional_task_number ? /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Optional task:"
                                    }),
                                    ' ',
                                    nc.optional_task_number
                                ]
                            }) : null,
                            (nc.pop_start_date || nc.pop_end_date) && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "PoP:"
                                    }),
                                    " ",
                                    nc.pop_start_date || '—',
                                    " →",
                                    ' ',
                                    nc.pop_end_date || '—'
                                ]
                            }),
                            typeof nc.funded_amount === 'number' && nc.funded_amount > 0 && /*#__PURE__*/ _jsxs("span", {
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-muted-foreground",
                                        children: "Obligated:"
                                    }),
                                    ' ',
                                    formatMoney(nc.funded_amount)
                                ]
                            })
                        ]
                    }),
                    nc.description && /*#__PURE__*/ _jsx("p", {
                        className: "whitespace-pre-wrap text-muted-foreground",
                        children: cleanDescription(nc.description)
                    })
                ]
            }),
            calendarImpact && /*#__PURE__*/ _jsx("p", {
                className: "mb-3 rounded border bg-background p-2.5 text-xs text-muted-foreground",
                "data-testid": Testids.modificationCenter.calendarImpact,
                "data-deliverable-field": field,
                children: calendarImpact
            }),
            d.narrative && /*#__PURE__*/ _jsx("p", {
                className: "mb-2 text-xs text-muted-foreground",
                children: d.narrative
            }),
            isPeriod && !key && /*#__PURE__*/ _jsxs("div", {
                className: "mb-3 space-y-1.5 rounded border bg-background p-2.5 text-xs",
                children: [
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-muted-foreground",
                        children: "This period change carries no target. Pick the period it applies to, then accept it — until then it can only be left blank."
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap items-end gap-2",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraSelect, {
                                size: "sm",
                                className: "w-auto",
                                "aria-label": "Target period",
                                value: targetPeriodId,
                                onChange: (e)=>setTargetPeriodId(e.target.value),
                                "data-testid": Testids.modificationCenter.targetPeriodSelect,
                                children: [
                                    /*#__PURE__*/ _jsx("option", {
                                        value: "",
                                        children: "Choose the period this change applies to…"
                                    }),
                                    periods.map((p)=>/*#__PURE__*/ _jsx("option", {
                                            value: p.id,
                                            children: periodOptionLabel(p)
                                        }, p.id))
                                ]
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                disabled: !targetPeriodId || isViewOnly || setTarget.isPending,
                                "data-testid": Testids.modificationCenter.targetPeriodSubmit,
                                onClick: ()=>setTarget.mutate({
                                        changeSetId,
                                        discrepancyId: d.id,
                                        periodId: targetPeriodId
                                    }),
                                children: "Set target period"
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-end gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Decision",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            size: "sm",
                            className: "w-auto",
                            value: decision,
                            onChange: (e)=>{
                                const next = e.target.value;
                                setDecision(next);
                                if (next !== 'Corrected') setCorrectedValue('');
                            },
                            "data-testid": Testids.modificationCenter.decisionSelect,
                            "data-no-writer": hasNoWriter(d) || undefined,
                            children: decisions.map((r)=>/*#__PURE__*/ _jsx("option", {
                                    value: r,
                                    children: r === 'Waived' ? 'Leave blank' : r
                                }, r))
                        })
                    }),
                    decision === 'Corrected' && /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Corrected value",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraInput, {
                            type: correctedInputType,
                            step: correctedInputType === 'number' ? 'any' : undefined,
                            value: correctedValue,
                            onChange: (e)=>setCorrectedValue(e.target.value),
                            "data-testid": Testids.modificationCenter.correctedValueInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        className: "flex-1",
                        label: "Rationale",
                        required: true,
                        children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                            value: rationale,
                            onChange: (e)=>setRationale(e.target.value),
                            "data-testid": Testids.modificationCenter.rationaleInput
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        disabled: !rationale.trim() || resolve.isPending || isViewOnly || decision === 'Corrected' && serializedCorrected === '',
                        "data-testid": Testids.modificationCenter.decisionSubmit,
                        onClick: ()=>resolve.mutate({
                                changeSetId,
                                discrepancyId: d.id,
                                resolutionState: decision,
                                decisionRationale: rationale.trim(),
                                ...decision === 'Corrected' ? {
                                    decidedValueJson: serializedCorrected
                                } : {}
                            }),
                        children: "Resolve"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-2 flex items-end gap-3",
                children: [
                    /*#__PURE__*/ _jsx(SpecraFormField, {
                        label: "Re-route to",
                        children: /*#__PURE__*/ _jsx(SpecraSelect, {
                            size: "sm",
                            className: "w-auto",
                            value: owner,
                            onChange: (e)=>setOwner(e.target.value),
                            "data-testid": Testids.modificationCenter.routeSelect,
                            children: FUNCTIONAL_OWNERS.map((o)=>/*#__PURE__*/ _jsx("option", {
                                    value: o,
                                    children: o
                                }, o))
                        })
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        size: "sm",
                        variant: "secondary",
                        disabled: owner === d.functionalOwner || route.isPending || isViewOnly,
                        "data-testid": Testids.modificationCenter.routeSubmit,
                        onClick: ()=>route.mutate({
                                changeSetId,
                                discrepancyId: d.id,
                                functionalOwner: owner
                            }),
                        children: "Route"
                    })
                ]
            })
        ]
    });
}
